import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Formik, Form } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import { createTenantInputSchema } from "@stw/yup-validators";

import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { useErrorMessageFormatter } from "~frontend/util/errors";

export default function AddTenantDialog({
  open,
  onClose,
  onSubmit,
  initialValues,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: {
    name: string;
    slug: string;
    desiredPartition: number | null;
    orgOwnerEmail: string | null;
    orgOwnerName: string | null;
  }) => Promise<void>;
  initialValues: {
    name: string;
    slug: string;
    orgOwnerEmail: string;
    orgOwnerName: string;
  };
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  return (
    <Dialog open={open}>
      <DialogTitle>Add Tenant</DialogTitle>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          reservedPartition: "",
          ...initialValues,
        }}
        validationSchema={createTenantInputSchema(Yup).fields.input}
        onSubmit={async (values, formik) => {
          try {
            await onSubmit({
              name: values.name,
              slug: values.slug,
              orgOwnerEmail:
                values.orgOwnerEmail.trim().length > 0
                  ? values.orgOwnerEmail
                  : null,
              orgOwnerName:
                values.orgOwnerName.trim().length > 0
                  ? values.orgOwnerName
                  : null,
              desiredPartition:
                values.reservedPartition.trim().length > 0
                  ? parseInt(values.reservedPartition)
                  : null,
            });
            formik.resetForm();
          } catch (e) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(e),
            });
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent sx={{ minWidth: "400px" }}>
              <p>
                This will add a new tenant. A tenant is a brand new client, with
                their own separate domain name.
              </p>
              <FormikErrorBanner />
              <FormikTextField
                name="name"
                label="Name"
                fullWidth
                autoComplete="off"
                inputProps={{ "data-testid": "add-tenant-name-field" }}
              />
              <FormikTextField
                name="slug"
                label="Slug / Subdomain"
                fullWidth
                autoComplete="off"
                inputProps={{ "data-testid": "add-tenant-slug-field" }}
              />
              <FormikTextField
                name="orgOwnerEmail"
                label="Org Owner Email (optional)"
                fullWidth
                type="email"
                autoComplete="off"
              />
              <FormikTextField
                name="orgOwnerName"
                label="Org Owner Name (optional)"
                fullWidth
                autoComplete="off"
              />
              <FormikTextField
                name="reservedPartition"
                InputProps={{
                  inputProps: {
                    min: 0,
                    max: 63,
                    step: 1,
                    inputMode: "numeric",
                  },
                }}
                label="Reserved Partiton (optional)"
                bottomLabel="For very large tenant (top 10), you can reserve a partition. Talk to the developers for more information."
                fullWidth
              />
            </DialogContent>

            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText="Add"
                loadingText="Adding..."
                data-testid="add-tenant-button"
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
